import type { ProjectRole, ToolboxCapability } from "@ttc-spectree/toolbox-contract/roles";

/**
 * What each per-project SpecTree Toolbox role allows, in plain words, for the
 * Roles page (epic PI-SPEC-SW-E294, feature PI-SPEC-SW-1509, UX-PM-14, frame
 * F4 in Claude Design "PM Packages B E F Mockups").
 *
 * The words are written from the roles matrix (`ROLE_CAPABILITIES` in
 * `@ttc-spectree/toolbox-contract/roles`, 01-roles.md section 4.2), not
 * computed from it. {@link ROLE_CLAIMS} lists the capabilities each role's
 * text asserts, and a test holds it equal to the matrix, so a matrix change
 * fails the build until the copy here is updated.
 *
 * Nothing here says the person is told about a role change: no notification
 * channel exists (UX-PM-14).
 */

/** One short line per role, shown under the role name in the Add-a-person role select. */
export const ROLE_LINES: Readonly<Record<ProjectRole, string>> = {
  "project-manager":
    "Runs grooming, writes ADO items, uses the FogBugz tools, brings FogBugz cases to the ADO board, asks about the product, sets the meeting schedule and product areas, sees how grooming is going, gives the other roles",
  "product-owner":
    "Grooms single items, posts own briefs, writes ADO items, uses the FogBugz tools, asks about the product, backup host; sees how grooming is going",
  developer: "Writes and improves ADO items, uses the FogBugz tools, asks about the product; reads the backlog and briefs",
  "qa-tester": "Reports bugs, writes ADO items, uses the FogBugz tools, asks about the product; reads the backlog and briefs",
  viewer: "Reads the backlog, briefs, how grooming is going. No Claude seat needed",
};

/** One sentence or two per role, shown in the "What each role can do" card. */
export const ROLE_CARD: Readonly<Record<ProjectRole, string>> = {
  "project-manager":
    "Runs grooming: agenda, briefs, Present, posting; writes ADO items; uses the FogBugz tools; brings FogBugz cases to the ADO board; asks about the product; sets the meeting schedule and product areas; sees how grooming is going; gives the four roles below. Given by an Admin.",
  "product-owner":
    "Grooms single items and posts their own briefs, writes ADO items, uses the FogBugz tools, asks about the product, sees how grooming is going, backup host in Present.",
  developer: "Writes and improves ADO items, uses the FogBugz tools, asks about the product; reads the backlog and briefs. Cannot groom.",
  "qa-tester": "Reports bugs, writes ADO items, uses the FogBugz tools and asks about the product; reads the backlog and briefs.",
  viewer: "Reads the backlog, the briefs and how grooming is going; changes nothing. For leadership, support and other teams.",
};

/** The last line of the "What each role can do" card: Admin is never given on this page. */
export const ADMIN_CARD_NOTE = "Admins are SpecTree global admins; they are managed in SpecTree, not here.";

/**
 * The capabilities each role's {@link ROLE_LINES} and {@link ROLE_CARD} text
 * asserts, in `TOOLBOX_CAPABILITIES` order. Must equal `ROLE_CAPABILITIES`
 * for every project role (tested): change the words and this list together.
 */
export const ROLE_CLAIMS: Readonly<Record<ProjectRole, readonly ToolboxCapability[]>> = {
  // "Runs grooming: agenda, briefs, Present, posting; writes ADO items; uses the FogBugz tools; brings FogBugz cases to the ADO
  // board; asks about the product; sets the meeting schedule and product areas; sees how grooming is going; gives the four roles
  // below." Not the repositories (Admin).
  "project-manager": [
    "prepare-sessions",
    "manage-agenda",
    "map-project",
    "groom-one",
    "post-single-brief",
    "formulator",
    "fogbugz",
    "fogbugz-to-ado",
    "ask",
    "read-backlog",
    "read-briefs",
    "read-grooming-progress",
    "present",
    "assign-roles",
  ],
  // "Grooms single items and posts their own briefs, writes ADO items, uses the FogBugz tools, asks about the product, sees how
  // grooming is going, backup host in Present."
  "product-owner": [
    "groom-one",
    "post-single-brief",
    "formulator",
    "fogbugz",
    "ask",
    "read-backlog",
    "read-briefs",
    "read-grooming-progress",
    "present",
  ],
  // "Writes and improves ADO items, uses the FogBugz tools, asks about the product; reads the backlog and briefs. Cannot groom."
  developer: ["formulator", "fogbugz", "ask", "read-backlog", "read-briefs"],
  // "Reports bugs, writes ADO items, uses the FogBugz tools and asks about the product; reads the backlog and briefs."
  "qa-tester": ["formulator", "fogbugz", "ask", "read-backlog", "read-briefs"],
  // "Reads the backlog, the briefs and how grooming is going; changes nothing."
  viewer: ["read-backlog", "read-briefs", "read-grooming-progress"],
};
